// src/pages/Setores.js - VERSÃO CORRIGIDA
import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Grid, Button, TextField, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Chip, CircularProgress, Alert, IconButton, Tooltip, Dialog,
  DialogTitle, DialogContent, DialogActions, Switch, FormControlLabel, Avatar
} from '@mui/material';
import {
  Business as BusinessIcon, Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  Search as SearchIcon, Refresh as RefreshIcon, ToggleOn as ActiveIcon,
  ToggleOff as InactiveIcon, Group as GroupIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const Setores = () => {
  const [setores, setSetores] = useState([]);
  const [funcionarios, setFuncionarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [busca, setBusca] = useState('');
  const [dialogAberto, setDialogAberto] = useState(false);
  const [setorEditando, setSetorEditando] = useState(null);
  const [dadosFormulario, setDadosFormulario] = useState({ nome: '', ativo: true });
  const [salvando, setSalvando] = useState(false);

  const { api, user } = useAuth();

  const carregarDados = async () => {
    setLoading(true);
    setError('');
    try {
      const [resSetores, resFuncionarios] = await Promise.all([
        api.listarSetores(),
        api.listarFuncionarios()
      ]);

      if (resSetores.success) {
        setSetores(resSetores.dados || []);
      } else {
        setError(resSetores.message || 'Erro ao carregar setores');
      }

      if (resFuncionarios.success) {
        setFuncionarios(resFuncionarios.dados || []);
      }

    } catch (err) {
      setError(err.message || 'Erro de conexão ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const abrirDialogAdicionar = () => {
    setSetorEditando(null);
    setDadosFormulario({ nome: '', ativo: true });
    setDialogAberto(true);
  };

  const abrirDialogEditar = (setor) => {
    setSetorEditando(setor);
    setDadosFormulario({ 
      nome: setor.nome || '', 
      ativo: setor.ativo === 1 || setor.ativo === true 
    });
    setDialogAberto(true);
  };

  const validarFormulario = () => {
    if (!dadosFormulario.nome.trim()) {
      setError('Nome do setor é obrigatório');
      return false;
    }
    
    return true;
  };

  const salvarSetor = async () => {
    // Limpar mensagens anteriores
    setError('');
    setSuccess('');
    
    if (!validarFormulario()) {
      return; // Para aqui se a validação falhar
    }

    setSalvando(true);

    try {
      const dadosParaEnviar = {
        ...dadosFormulario,
        ativo: dadosFormulario.ativo ? 1 : 0
      };

      let response;
      if (setorEditando) {
        response = await api.atualizarSetor(setorEditando.id, dadosParaEnviar);
      } else {
        response = await api.criarSetor(dadosParaEnviar);
      }
      
      if (response.success) {
        setSuccess(response.message || 'Setor salvo com sucesso!');
        
        // Aguardar um pouco para mostrar a mensagem de sucesso, depois fechar o modal
        setTimeout(() => {
          setDialogAberto(false);
          setSuccess(''); // Limpar a mensagem do modal
          carregarDados(); // Recarregar lista
        }, 2000);
      } else {
        throw new Error(response.message || 'Erro desconhecido');
      }

    } catch (err) {
      setError(err.message || 'Erro ao salvar setor');
    } finally {
      setSalvando(false);
    }
  };

  const excluirSetor = async (setor) => {
    const funcionariosNoSetor = contarFuncionarios(setor.nome);
    if (funcionariosNoSetor > 0) {
      setError(`Não é possível excluir o setor "${setor.nome}" pois há ${funcionariosNoSetor} funcionário(s) vinculado(s)`);
      return;
    }

    if (!window.confirm(`Tem certeza que deseja excluir o setor "${setor.nome}"?`)) {
      return;
    }

    setError('');
    setSuccess('');

    try {
      const response = await api.excluirSetor(setor.id);

      if (response.success) {
        setSuccess(response.message || 'Setor excluído com sucesso!');
        await carregarDados();
      } else {
        throw new Error(response.message || 'Erro ao excluir setor');
      }

    } catch (err) {
      setError(err.message || 'Erro ao excluir setor');
    }
  };

  const alternarStatus = async (setor) => {
    try {
      const novoStatus = setor.ativo === 1 ? 0 : 1;
      const dadosParaEnviar = {
        nome: setor.nome,
        ativo: novoStatus
      };

      const response = await api.atualizarSetor(setor.id, dadosParaEnviar);

      if (response.success) {
        setSuccess(`Status do setor "${setor.nome}" alterado com sucesso!`);
        await carregarDados();
      } else {
        throw new Error(response.message || 'Erro ao alterar status');
      }

    } catch (err) {
      setError(err.message || 'Erro ao alterar status do setor');
    }
  };

  const contarFuncionarios = (nomeSetor) => {
    return funcionarios.filter(func => func.setor === nomeSetor).length;
  };

  const setoresFiltrados = setores.filter(setor => 
    (setor.nome?.toLowerCase() || '').includes(busca.toLowerCase())
  );

  const estatisticas = {
    total: setores.length,
    ativos: setores.filter(s => s.ativo === 1).length,
    inativos: setores.filter(s => s.ativo === 0).length,
    comFuncionarios: setores.filter(s => contarFuncionarios(s.nome) > 0).length
  };

  const formatarData = (dataString) => {
    if (!dataString || dataString === 'N/A' || dataString === 'Invalid Date') {
      return 'N/A';
    }
    try {
      const data = new Date(dataString);
      // Verificar se a data é válida
      if (isNaN(data.getTime())) {
        return 'N/A';
      }
      return data.toLocaleDateString('pt-BR');
    } catch {
      return 'N/A';
    }
  };

  const coresAvatar = ['#6366f1', '#8b5cf6', '#ec4899', '#ef4444', '#f97316', '#eab308', '#22c55e', '#10b981', '#06b6d4', '#3b82f6'];

  useEffect(() => {
    carregarDados();
  }, []);

  return (
    <Box sx={{ p: 3, backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      {/* Header */}
      <Paper elevation={0} sx={{ 
        p: 3, mb: 3, 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
        color: 'white', 
        borderRadius: 2 
      }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              🏢 Gestão de Setores
            </Typography>
            <Typography variant="subtitle1" opacity={0.9}>
              Organização e estrutura da empresa
            </Typography>
          </Box>
          {user.nivel >= 3 && (
            <Button 
              variant="contained" 
              startIcon={<AddIcon />} 
              onClick={abrirDialogAdicionar}
              sx={{ 
                backgroundColor: 'rgba(255,255,255,0.2)', 
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.3)' } 
              }}
            >
              Novo Setor
            </Button>
          )}
        </Box>
      </Paper>

      {/* Alertas */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Cards de Estatísticas */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={0} sx={{ 
            p: 2, 
            textAlign: 'center', 
            borderRadius: 2, 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
            color: 'white' 
          }}>
            <Typography variant="h4" fontWeight="bold">
              {estatisticas.total}
            </Typography>
            <Typography variant="body2" opacity={0.9}>
              Total de Setores
            </Typography>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={0} sx={{ 
            p: 2, 
            textAlign: 'center', 
            borderRadius: 2, 
            background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)', 
            color: 'white' 
          }}>
            <Typography variant="h4" fontWeight="bold">
              {estatisticas.ativos}
            </Typography>
            <Typography variant="body2" opacity={0.9}>
              Setores Ativos
            </Typography>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={0} sx={{ 
            p: 2, 
            textAlign: 'center', 
            borderRadius: 2, 
            background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)', 
            color: 'white' 
          }}>
            <Typography variant="h4" fontWeight="bold">
              {estatisticas.inativos}
            </Typography>
            <Typography variant="body2" opacity={0.9}>
              Setores Inativos
            </Typography>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={0} sx={{ 
            p: 2, 
            textAlign: 'center', 
            borderRadius: 2, 
            background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)', 
            color: 'white' 
          }}>
            <Typography variant="h4" fontWeight="bold">
              {estatisticas.comFuncionarios}
            </Typography>
            <Typography variant="body2" opacity={0.9}>
              Com Funcionários
            </Typography>
          </Card>
        </Grid>
      </Grid>

      {/* Campo de Busca */}
      <Card elevation={0} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <TextField 
          fullWidth 
          placeholder="Buscar setores..." 
          value={busca} 
          onChange={(e) => setBusca(e.target.value)} 
          InputProps={{ 
            startAdornment: <SearchIcon sx={{ mr: 1, color: '#64748b' }} /> 
          }} 
        />
      </Card>

      {/* Lista de Setores */}
      <Card elevation={0} sx={{ borderRadius: 2 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" fontWeight="bold">
              📋 Lista de Setores
            </Typography>
            <IconButton onClick={carregarDados} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Box>
          
          {loading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : setoresFiltrados.length > 0 ? (
            <TableContainer component={Paper} elevation={0}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>Setor</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Funcionários</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Criado em</TableCell>
                    {user.nivel >= 3 && (
                      <TableCell sx={{ fontWeight: 'bold' }} align="center">Ações</TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {setoresFiltrados.map((setor, index) => (
                    <TableRow key={setor.id} hover>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <Avatar sx={{ 
                            mr: 2, 
                            backgroundColor: coresAvatar[index % coresAvatar.length], 
                            width: 40, 
                            height: 40 
                          }}>
                            <BusinessIcon />
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2" fontWeight="bold">
                              {setor.nome}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              ID: {setor.id}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          {user.nivel >= 3 ? (
                            <FormControlLabel 
                              control={
                                <Switch 
                                  checked={setor.ativo === 1} 
                                  onChange={() => alternarStatus(setor)} 
                                  size="small" 
                                />
                              } 
                              label={setor.ativo === 1 ? 'Ativo' : 'Inativo'} 
                            />
                          ) : (
                            <Chip 
                              label={setor.ativo === 1 ? 'Ativo' : 'Inativo'} 
                              color={setor.ativo === 1 ? 'success' : 'error'} 
                              size="small" 
                              variant="outlined" 
                              icon={setor.ativo === 1 ? <ActiveIcon /> : <InactiveIcon />} 
                            />
                          )}
                        </Box>
                      </TableCell>
                      
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <GroupIcon sx={{ mr: 1, fontSize: 16, color: '#10b981' }} />
                          <Typography variant="body2">
                            {contarFuncionarios(setor.nome)} funcionário{contarFuncionarios(setor.nome) !== 1 ? 's' : ''}
                          </Typography>
                        </Box>
                      </TableCell>
                      
                      <TableCell>
                        <Typography variant="body2" color="textSecondary">
                          {formatarData(setor.created_at)}
                        </Typography>
                      </TableCell>
                      
                      {user.nivel >= 3 && (
                        <TableCell align="center">
                          <Box display="flex" gap={1} justifyContent="center">
                            <IconButton 
                              size="small" 
                              onClick={() => abrirDialogEditar(setor)} 
                              color="primary"
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton 
                              size="small" 
                              onClick={() => excluirSetor(setor)} 
                              color="error"
                              disabled={contarFuncionarios(setor.nome) > 0}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box textAlign="center" py={6}>
              <BusinessIcon sx={{ fontSize: 60, color: '#e2e8f0', mb: 2 }} />
              <Typography variant="h6" color="textSecondary">
                {busca ? 'Nenhum setor encontrado' : 'Nenhum setor cadastrado'}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {busca ? 'Tente alterar os termos da busca' : 'Clique em "Novo Setor" para começar'}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Dialog para Adicionar/Editar Setor */}
      <Dialog 
        open={dialogAberto} 
        onClose={() => setDialogAberto(false)} 
        maxWidth="sm" 
        fullWidth
        style={{ zIndex: 1300 }} // Garantir que fique na frente
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <BusinessIcon color="primary" />
            <Typography variant="h6">
              {setorEditando ? 'Editar Setor' : 'Novo Setor'}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {/* Mostrar erro dentro do modal */}
          {error && (
            <Alert 
              severity="error" 
              sx={{ mb: 2 }} 
              onClose={() => setError('')}
            >
              {error}
            </Alert>
          )}
          
          {/* Mostrar sucesso dentro do modal */}
          {success && (
            <Alert 
              severity="success" 
              sx={{ mb: 2 }} 
              onClose={() => setSuccess('')}
            >
              {success}
            </Alert>
          )}
          
          <Box sx={{ mt: 2 }}>
            <TextField 
              fullWidth 
              label="Nome do Setor *" 
              value={dadosFormulario.nome} 
              onChange={(e) => setDadosFormulario(prev => ({ 
                ...prev, 
                nome: e.target.value 
              }))} 
              sx={{ mb: 2 }} 
            />
            
            <FormControlLabel 
              control={
                <Switch 
                  checked={dadosFormulario.ativo} 
                  onChange={(e) => setDadosFormulario(prev => ({ 
                    ...prev, 
                    ativo: e.target.checked 
                  }))} 
                />
              } 
              label="Setor ativo" 
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button 
            onClick={() => setDialogAberto(false)} 
            disabled={salvando}
          >
            Cancelar
          </Button>
          <Button 
            variant="contained" 
            onClick={salvarSetor} 
            disabled={salvando}
            startIcon={salvando ? <CircularProgress size={20} /> : null}
          >
            {salvando ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Setores;